import { Injectable } from '@angular/core';

import {
    IProfilePoint,
    RISER_BOARD_THICKNESS,
    STRINGER_INSET,
    STRINGER_THICKNESS,
    TETIVA_PROTRUSION,
    TREAD_SIDE_OVERHANG,
    exitRiserExtent,
    kosourProfile,
    kosourRunAxis,
    riserExtent,
    riserSpanY,
    tetivaProfile,
    winderRiserBoardOutline,
    winderTreadOutline,
} from '@stair-craft/calc/util';
import {
    EPlankKind,
    IBalusterGeometry,
    IPlankGeometry,
    IRailingGeometry,
    IRailingMaterialIds,
    IStairGeometry,
    IStepGeometry,
    IStringerGeometry,
    IStringerRunSpan,
    IStringerType,
    ISupportPosts,
    IVec3,
    IWoodMaterial,
    WOOD_MATERIALS,
} from '@stair-craft/common/util';
import {
    BoxGeometry,
    BufferAttribute,
    BufferGeometry,
    Color,
    CylinderGeometry,
    ExtrudeGeometry,
    Float32BufferAttribute,
    Group,
    LineBasicMaterial,
    MathUtils,
    Mesh,
    MeshStandardMaterial,
    Shape,
    Texture,
    Vector2,
} from 'three';

import { addEdgeLines, createEdgeMaterial } from './edge-lines';
import { buildHandrailSweepGeometry, handrailChains, isUprightChain } from './handrail-sweep';
import { WOOD_TILE_MM, buildBlockGeometry, buildPlanPrismGeometry } from './stair-board-geometry';
import { applyBoxWoodUV, applyExtrudeWoodUV, createWoodTexture } from './wood-grain';

/** Shifts the texture tile from board to board (in tiles), so equal treads do not repeat one pattern. */
const BOARD_UV_SHIFT: number = 0.37;

interface IPlanarPoint {
    x: number;
    z: number;
}

/**
 * Extracted from `#buildWinderTread` to keep its complexity low: the tread outline in XZ — the
 * shared `winderTreadOutline` (leading edge nosed, duplicates dropped) — wound clockwise.
 */
function buildWinderTreadPolygon(corners: NonNullable<IStepGeometry['winderCorners']>, nose: number): IPlanarPoint[] {
    const polygon: IPlanarPoint[] = winderTreadOutline(corners, nose);

    // Ensure CW winding in XZ for top face normal +Y
    let area2: number = 0;
    for (let i: number = 0; i < polygon.length; i++) {
        const a: IPlanarPoint = polygon[i];
        const b: IPlanarPoint = polygon[(i + 1) % polygon.length];
        area2 += a.x * b.z - b.x * a.z;
    }
    if (area2 > 0) {
        polygon.reverse();
    } // CCW → reverse to CW

    return polygon;
}

/**
 * Extracted from `#buildWinderTread` to keep its complexity low: emits the
 * triangle-fan top/bottom faces and the perimeter quad sides for the tread.
 */
function buildWinderTreadBuffers(
    polygon: IPlanarPoint[],
    yTop: number,
    yBot: number,
    treadThickness: number
): { positions: number[]; uvs: number[]; faceIndices: number[] } {
    const n: number = polygon.length;
    const positions: number[] = [];
    const uvs: number[] = [];
    const faceIndices: number[] = [];
    let vi: number = 0;

    // Top face — triangle fan. UVs: planar projection to XZ plane.
    for (const p of polygon) {
        positions.push(p.x, yTop, p.z);
        uvs.push(p.x / WOOD_TILE_MM, p.z / WOOD_TILE_MM);
    }
    for (let i: number = 1; i < n - 1; i++) {
        faceIndices.push(vi, vi + i, vi + i + 1);
    }
    vi += n;

    // Bottom face — triangle fan (reversed winding)
    for (const p of polygon) {
        positions.push(p.x, yBot, p.z);
        uvs.push(p.x / WOOD_TILE_MM, p.z / WOOD_TILE_MM);
    }
    for (let i: number = 1; i < n - 1; i++) {
        faceIndices.push(vi, vi + i + 1, vi + i);
    }
    vi += n;

    // Side faces — quads with UV along perimeter for grain flowing around the board edge.
    let perimAcc: number = 0;
    for (let i: number = 0; i < n; i++) {
        const j: number = (i + 1) % n;
        const pi: IPlanarPoint = polygon[i];
        const pj: IPlanarPoint = polygon[j];
        const segLen: number = Math.sqrt((pj.x - pi.x) ** 2 + (pj.z - pi.z) ** 2);
        const u0: number = perimAcc / WOOD_TILE_MM;
        const u1: number = (perimAcc + segLen) / WOOD_TILE_MM;
        const v1: number = treadThickness / WOOD_TILE_MM;
        positions.push(pi.x, yTop, pi.z, pj.x, yTop, pj.z, pj.x, yBot, pj.z, pi.x, yBot, pi.z);
        uvs.push(u0, v1, u1, v1, u1, 0, u0, 0);
        // Wound so the side faces outward, like the board prisms (`buildPlanPrismGeometry`).
        faceIndices.push(vi, vi + 2, vi + 1, vi, vi + 3, vi + 2);
        vi += 4;
        perimAcc += segLen;
    }

    return { positions, uvs, faceIndices };
}

/**
 * Extracted from `#buildKosourStringer` to keep its complexity low: maps a
 * stringer side to the sign of its lateral offset (+1 left, -1 right, 0 center).
 */
function stringerSideSign(side: IStringerGeometry['side']): number {
    if (side === 'left') {
        return 1;
    }
    if (side === 'right') {
        return -1;
    }
    return 0;
}

/** Slope of a stringer in its side-profile plane (run, y), first point to last: the grain follows it. */
function boardSlope(points: IVec3[], runOf: (p: IVec3) => number): number {
    const first: IVec3 = points[0];
    const last: IVec3 = points[points.length - 1];
    return Math.atan2(last.y - first.y, runOf(last) - runOf(first));
}

/** The kosour side profile from the shared board profile, as a shape in (run, y). */
function buildKosourShape(
    points: IVec3[],
    width: number,
    bottomSlope: { rise: number; run: number } | undefined,
    runSpan: IStringerRunSpan | undefined
): Shape {
    const outline: IProfilePoint[] = kosourProfile(points, width, bottomSlope, runSpan);
    return new Shape(outline.map((point: IProfilePoint) => new Vector2(point.run, point.y)));
}

interface IStepMaterials {
    readonly treadMat: MeshStandardMaterial;
    readonly riserMat: MeshStandardMaterial;
    readonly treadMatId: string;
    readonly riserMatId: string;
    readonly stringerType: IStringerType;
    readonly risers: boolean;
}

/** A straight railing bar between two centre points: a handrail run or a plank. */
interface IRailBar {
    readonly from: IVec3;
    readonly to: IVec3;
    readonly width: number;
    readonly height: number;
}

interface IRiserPlacement {
    readonly yTop: number;
    readonly yBot: number;
    /** Stair-local Z (before rotation) of the riser's front face. */
    readonly z: number;
    /** Where the board runs along the tread width, from `position.x`. */
    readonly extent: [number, number];
}

@Injectable()
export class StairMeshBuilder {
    readonly #materialCache: Map<string, MeshStandardMaterial> = new Map<string, MeshStandardMaterial>();
    readonly #textureCache: Map<string, Texture | null> = new Map<string, Texture | null>();
    #edgeMaterial: LineBasicMaterial | null = null;

    public buildAll(
        geometry: IStairGeometry,
        treadMatId: string,
        stringerMatId: string,
        riserMatId: string,
        stringerType: IStringerType,
        warningSteps: Set<number> = new Set<number>(),
        railingMatIds: IRailingMaterialIds = { handrailMaterialId: treadMatId, balusterMaterialId: treadMatId }
    ): { stairGroup: Group; upperFloorGroup: Group } {
        const stairGroup: Group = new Group();
        const upperFloorGroup: Group = new Group();

        const treadMat: MeshStandardMaterial = this.#getMaterial(treadMatId);
        const stringerMat: MeshStandardMaterial = this.#getMaterial(stringerMatId);
        const riserMat: MeshStandardMaterial = this.#getMaterial(riserMatId);

        this.#addStepMeshes(
            stairGroup,
            geometry.steps,
            { treadMat, riserMat, treadMatId, riserMatId, stringerType, risers: geometry.risers },
            warningSteps
        );
        this.#addStringerMeshes(stairGroup, geometry.stringers, stringerMat, stringerType);
        this.#addSupportPostMeshes(stairGroup, geometry.supports, stringerMat);
        // Railings: stair railings move with the stair group; upper-floor railings stay anchored
        // to the opening in world coords so changing stair angle doesn't drag them off the slab.
        this.#addRailingMeshes(geometry.railings, stairGroup, upperFloorGroup, railingMatIds);

        return { stairGroup, upperFloorGroup };
    }

    /** Extracted from `buildAll` to keep its complexity low: adds tread + riser meshes per step. */
    #addStepMeshes(stairGroup: Group, steps: IStepGeometry[], materials: IStepMaterials, warningSteps: Set<number>): void {
        const { treadMatId, riserMatId, stringerType } = materials;
        for (const step of steps) {
            const mat: MeshStandardMaterial = warningSteps.has(step.index) ? this.#getWarningMaterial(treadMatId) : materials.treadMat;
            const rMat: MeshStandardMaterial = warningSteps.has(step.index) ? this.#getWarningMaterial(riserMatId) : materials.riserMat;
            const edgeMat: LineBasicMaterial = this.#getEdgeMaterial();
            const tread: Mesh =
                step.isWinder && step.winderCorners
                    ? this.#buildWinderTread(step, step.winderCorners, mat)
                    : this.#buildTread(step, mat, stringerType);
            const meshes: (Mesh | null)[] = materials.risers ? [tread, ...this.#buildStepRisers(step, rMat, stringerType)] : [tread];
            meshes.forEach((mesh: Mesh | null) => {
                if (mesh) {
                    stairGroup.add(addEdgeLines(mesh, edgeMat));
                }
            });
        }
    }

    /** Extracted from `buildAll` to keep its complexity low: adds stringer meshes (splitting тетива pairs). */
    #addStringerMeshes(
        stairGroup: Group,
        stringers: IStringerGeometry[],
        stringerMat: MeshStandardMaterial,
        stringerType: IStringerType
    ): void {
        const isKosour: boolean = stringerType === 'kosour';
        const edgeMat: LineBasicMaterial = this.#getEdgeMaterial();
        for (const stringer of stringers) {
            if (!isKosour && !stringer.bottom && !stringer.block && stringer.points.length === 3) {
                for (let i: number = 0; i < 2; i++) {
                    const part: IStringerGeometry = { ...stringer, points: [stringer.points[i], stringer.points[i + 1]] };
                    stairGroup.add(addEdgeLines(this.#buildStringer(part, stringerMat), edgeMat));
                }
            } else {
                stairGroup.add(addEdgeLines(this.#buildStringer(stringer, stringerMat), edgeMat));
            }
        }
    }

    /** The turn's support posts (SC-CALC-093): square boxes from the floor, of the load-bearing species. */
    #addSupportPostMeshes(stairGroup: Group, supports: ISupportPosts | undefined, stringerMat: MeshStandardMaterial): void {
        const edgeMat: LineBasicMaterial = this.#getEdgeMaterial();
        const width: number = supports?.width ?? 0;
        (supports?.posts ?? []).forEach((post: IBalusterGeometry, i: number) => {
            const geo: BoxGeometry = new BoxGeometry(width, post.height, width);
            applyBoxWoodUV(geo, [width, post.height, width], 'y', i * BOARD_UV_SHIFT);
            const mesh: Mesh = new Mesh(geo, stringerMat);
            mesh.position.set(post.position.x, post.position.y + post.height / 2, post.position.z);
            mesh.castShadow = true;
            mesh.name = `support-post-${String(i)}`;
            stairGroup.add(addEdgeLines(mesh, edgeMat));
        });
    }

    /**
     * Extracted from `buildAll` to keep its complexity low: routes railing meshes to the stair or
     * upper-floor group. The handrail takes its species, balusters and posts take theirs.
     */
    #addRailingMeshes(railings: IRailingGeometry[], stairGroup: Group, upperFloorGroup: Group, matIds: IRailingMaterialIds): void {
        const handrailMat: MeshStandardMaterial = this.#getHandrailMaterial(matIds.handrailMaterialId);
        const balusterMat: MeshStandardMaterial = this.#getBalusterMaterial(matIds.balusterMaterialId);
        for (const railing of railings) {
            const target: Group = railing.kind === 'upper' ? upperFloorGroup : stairGroup;
            this.#buildRailing(railing, balusterMat, handrailMat).forEach((mesh: Mesh) => target.add(mesh));
        }
    }

    #buildTread(step: IStepGeometry, material: MeshStandardMaterial, stringerType: IStringerType): Mesh {
        // On kosour boards a flight tread reaches past the boards so its end face never shares a
        // plane with them; a тетива houses the tread end, and a landing ends flush with its edge.
        const lateralOverhang: number = stringerType === 'kosour' && !step.isLanding ? TREAD_SIDE_OVERHANG : 0;
        const treadFullWidth: number = step.treadWidth + lateralOverhang * 2;
        const treadFullDepth: number = step.treadDepth + step.noseLength;
        const geo: BoxGeometry = new BoxGeometry(treadFullWidth, step.treadThickness, treadFullDepth);
        // Grain along X — length of the tread board (width of the stair).
        applyBoxWoodUV(geo, [treadFullWidth, step.treadThickness, treadFullDepth], 'x', step.index * BOARD_UV_SHIFT);

        const mesh: Mesh = new Mesh(geo, material);
        mesh.position.set(
            step.position.x + step.treadWidth / 2,
            step.position.y - step.treadThickness / 2,
            step.position.z + treadFullDepth / 2 - step.noseLength
        );
        mesh.castShadow = true;
        mesh.receiveShadow = true;
        mesh.name = `tread-${String(step.index)}`;

        if (step.rotation !== 0) {
            mesh.rotateY(MathUtils.degToRad(step.rotation));
        }

        return mesh;
    }

    #buildWinderTread(step: IStepGeometry, corners: NonNullable<IStepGeometry['winderCorners']>, material: MeshStandardMaterial): Mesh {
        const yTop: number = step.position.y;
        const yBot: number = yTop - step.treadThickness;

        // Extend Back edge by noseLength (overhang past riser), dedupe, ensure CW winding.
        const polygon: IPlanarPoint[] = buildWinderTreadPolygon(corners, step.noseLength);
        const { positions, uvs, faceIndices } = buildWinderTreadBuffers(polygon, yTop, yBot, step.treadThickness);

        const geo: BufferGeometry = new BufferGeometry();
        geo.setAttribute('position', new Float32BufferAttribute(positions, 3));
        geo.setAttribute('uv', new Float32BufferAttribute(uvs, 2));
        geo.setIndex(faceIndices);
        geo.computeVertexNormals();

        const mesh: Mesh = new Mesh(geo, material);
        mesh.castShadow = true;
        mesh.receiveShadow = true;
        mesh.name = `winder-tread-${String(step.index)}`;
        return mesh;
    }

    #buildWinderRiser(
        step: IStepGeometry,
        corners: NonNullable<IStepGeometry['winderCorners']>,
        material: MeshStandardMaterial
    ): Mesh | null {
        // Riser stands under its own tread, behind the leading edge: from the bottom of the tread
        // below (or the floor) up to the bottom of this tread.
        const { yTop, yBot } = riserSpanY(step);
        if (yTop - yBot < 1) {
            return null;
        }
        const outline: { x: number; z: number }[] = winderRiserBoardOutline(step, corners);
        const mesh: Mesh = new Mesh(
            buildPlanPrismGeometry(
                outline,
                (): number => yTop,
                (): number => yBot
            ),
            material
        );
        mesh.castShadow = true;
        mesh.name = `winder-riser-${String(step.index)}`;
        return mesh;
    }

    /** The riser boards of one step: a winder's own outline, or the board under the tread and the one behind the exit. */
    #buildStepRisers(step: IStepGeometry, material: MeshStandardMaterial, stringerType: IStringerType): (Mesh | null)[] {
        if (step.isWinder && step.winderCorners) {
            return [this.#buildWinderRiser(step, step.winderCorners, material)];
        }
        return [this.#buildRiser(step, material, stringerType), this.#buildExitRiser(step, material, stringerType)];
    }

    #buildRiser(step: IStepGeometry, material: MeshStandardMaterial, stringerType: IStringerType): Mesh | null {
        // Riser stands under its own tread, behind the leading edge (the nose overhangs its face by
        // exactly `noseLength`), from the bottom of the tread below — or the floor — up to the
        // bottom of this tread. The tread below butts against its face.
        const { yTop, yBot } = riserSpanY(step);
        const placement: IRiserPlacement = { yTop, yBot, extent: riserExtent(step, stringerType), z: step.position.z };
        return this.#buildRiserBoard(step, placement, material, `riser-${String(step.index)}`);
    }

    /** The closing riser behind the last tread, up to the slab bottom. */
    #buildExitRiser(step: IStepGeometry, material: MeshStandardMaterial, stringerType: IStringerType): Mesh | null {
        if (!step.exitRiserHeight || step.exitRiserHeight <= 0) {
            return null;
        }
        const yBot: number = step.position.y - step.treadThickness;
        const placement: IRiserPlacement = {
            yBot,
            extent: exitRiserExtent(step, stringerType),
            yTop: yBot + step.exitRiserHeight,
            z: step.position.z + step.treadDepth,
        };
        return this.#buildRiserBoard(step, placement, material, `riser-exit-${String(step.index)}`);
    }

    #buildRiserBoard(step: IStepGeometry, placement: IRiserPlacement, material: MeshStandardMaterial, name: string): Mesh | null {
        const height: number = placement.yTop - placement.yBot;
        const [start, end]: [number, number] = placement.extent;
        const width: number = end - start;
        if (height < 1 || width < 1) {
            return null;
        }
        const geo: BoxGeometry = new BoxGeometry(width, height, RISER_BOARD_THICKNESS);
        // Grain along X — board lies horizontally with grain along its length.
        applyBoxWoodUV(geo, [width, height, RISER_BOARD_THICKNESS], 'x', (step.index + 0.5) * BOARD_UV_SHIFT);

        // Offset geometry so it rotates around the tread center (same pivot as tread mesh)
        const treadCenterZ: number = (step.treadDepth + step.noseLength) / 2 - step.noseLength;
        geo.translate(start + width / 2 - step.treadWidth / 2, 0, placement.z - step.position.z + RISER_BOARD_THICKNESS / 2 - treadCenterZ);

        const mesh: Mesh = new Mesh(geo, material);
        mesh.position.set(step.position.x + step.treadWidth / 2, placement.yBot + height / 2, step.position.z + treadCenterZ);
        mesh.castShadow = true;
        mesh.name = name;

        if (step.rotation !== 0) {
            mesh.rotateY(MathUtils.degToRad(step.rotation));
        }

        return mesh;
    }

    #buildStringer(stringer: IStringerGeometry, material: MeshStandardMaterial): Mesh {
        if (stringer.points.length < 2) {
            return new Mesh();
        }

        // Central pole — render as cylinder
        if (stringer.side === 'center' && stringer.thickness === stringer.width) {
            return this.#buildCylinderPole(stringer, material);
        }

        // Turn-zone board segment under a winder or a landing edge
        if (stringer.block) {
            const blockMesh: Mesh = new Mesh(buildBlockGeometry(stringer.block), material);
            blockMesh.castShadow = true;
            blockMesh.name = `stringer-${stringer.side}-block`;
            return blockMesh;
        }

        // Тетива with an explicit bottom edge — a side profile cut at the floor
        if (stringer.bottom) {
            return this.#buildTetivaProfile(stringer, stringer.bottom, material);
        }

        // Косоур — multi-point zigzag profile, render as sawtooth-top board via ExtrudeGeometry
        if (stringer.points.length > 2) {
            return this.#buildKosourStringer(stringer, material);
        }

        const first: IVec3 = stringer.points[0];
        const last: IVec3 = stringer.points[stringer.points.length - 1];

        const dx: number = last.x - first.x;
        const dy: number = last.y - first.y;
        const dz: number = last.z - first.z;

        const horizontalRun: number = Math.sqrt(dx * dx + dz * dz);
        const length: number = Math.sqrt(horizontalRun * horizontalRun + dy * dy);
        const slopeAngle: number = Math.atan2(dy, horizontalRun);
        const headingAngle: number = Math.atan2(dx, dz);

        // Offset geometry so top edge aligns with the stringer line,
        // and the board extends downward (no overshoot above floorHeight).
        const geo: BoxGeometry = new BoxGeometry(stringer.thickness, stringer.width, length);
        // Grain along Z — along the board's length (slope direction after rotation).
        applyBoxWoodUV(geo, [stringer.thickness, stringer.width, length], 'z');
        geo.translate(0, -stringer.width / 2, 0);

        // Perpendicular offset in XZ plane based on side
        const perpX: number = Math.cos(headingAngle);
        const perpZ: number = -Math.sin(headingAngle);

        // Boards sit INSIDE the stair width — flush against the inner edge (slight inset to avoid
        // coplanar faces with the tread ends).
        const overhang: number = 2;
        const magnitude: number = stringer.thickness / 2 + overhang;
        let sideOffset: number;
        if (stringer.side === 'left') {
            sideOffset = magnitude;
        } else if (stringer.side === 'right') {
            sideOffset = -magnitude;
        } else {
            sideOffset = 0;
        }

        const mesh: Mesh = new Mesh(geo, material);
        mesh.position.set(first.x + dx / 2 + sideOffset * perpX, first.y + dy / 2, first.z + dz / 2 + sideOffset * perpZ);
        mesh.rotation.order = 'YXZ';
        mesh.rotation.y = headingAngle;
        mesh.rotation.x = -slopeAngle;
        mesh.castShadow = true;
        mesh.name = `stringer-${stringer.side}`;

        return mesh;
    }

    // Косоур — sawtooth-top board following the step profile.
    // Auto-detects the run axis: Z-axis flights (straight, L/U-flight1) vs X-axis flights (L-flight2).
    // Builds the side-profile shape in the (run, y) plane and extrudes perpendicular to it.
    #buildKosourStringer(stringer: IStringerGeometry, material: MeshStandardMaterial): Mesh {
        const { points, thickness, width } = stringer;

        const { runAlongX, runOf, fixedCoord } = kosourRunAxis(points);
        const shape: Shape = buildKosourShape(points, width, stringer.bottomSlope, stringer.runSpan);

        const geo: ExtrudeGeometry = new ExtrudeGeometry(shape, { depth: thickness, bevelEnabled: false });
        applyExtrudeWoodUV(geo, boardSlope(points, runOf));

        // Map shape (x=run, y=y) + extrude(z) to world axes:
        //   runAlongZ → rotateY(-π/2) so shape.x → world.z, extrude → world.x; recenter on X
        //   runAlongX → no rotation; shape.x → world.x already, extrude → world.z; recenter on Z
        if (!runAlongX) {
            geo.rotateY(-Math.PI / 2);
            geo.translate(thickness / 2, 0, 0);
        } else {
            geo.translate(0, 0, -thickness / 2);
        }

        // The board sits under the treads, STRINGER_INSET in from the stair edge.
        const magnitude: number = thickness / 2 + STRINGER_INSET;
        const sign: number = stringerSideSign(stringer.side);
        const offsetX: number = runAlongX ? 0 : sign * magnitude;
        const offsetZ: number = runAlongX ? sign * magnitude : 0;

        const mesh: Mesh = new Mesh(geo, material);
        if (runAlongX) {
            mesh.position.set(0, 0, fixedCoord + offsetZ);
        } else {
            mesh.position.set(fixedCoord + offsetX, 0, 0);
        }
        mesh.castShadow = true;
        mesh.name = `stringer-${stringer.side}-kosour`;
        return mesh;
    }

    // Тетива — a flat board between an explicit top and bottom edge, housing the tread ends:
    // it reaches TETIVA_PROTRUSION past the stair edge and runs STRINGER_THICKNESS inward.
    #buildTetivaProfile(stringer: IStringerGeometry, bottom: IVec3[], material: MeshStandardMaterial): Mesh {
        const { runAlongX, runOf, fixedCoord } = kosourRunAxis(stringer.points);
        const profile: IProfilePoint[] = tetivaProfile(stringer.points, bottom, runOf);
        const shape: Shape = new Shape(profile.map((point: IProfilePoint) => new Vector2(point.run, point.y)));
        const geo: ExtrudeGeometry = new ExtrudeGeometry(shape, { depth: stringer.thickness, bevelEnabled: false });
        applyExtrudeWoodUV(geo, boardSlope(stringer.points, runOf));
        if (!runAlongX) {
            geo.rotateY(-Math.PI / 2);
            geo.translate(stringer.thickness / 2, 0, 0);
        } else {
            geo.translate(0, 0, -stringer.thickness / 2);
        }
        const inward: number = stringerSideSign(stringer.side) * (STRINGER_THICKNESS / 2 - TETIVA_PROTRUSION);
        const mesh: Mesh = new Mesh(geo, material);
        if (runAlongX) {
            mesh.position.set(0, 0, fixedCoord + inward);
        } else {
            mesh.position.set(fixedCoord + inward, 0, 0);
        }
        mesh.castShadow = true;
        mesh.name = `stringer-${stringer.side}-tetiva`;
        return mesh;
    }

    #buildCylinderPole(stringer: IStringerGeometry, material: MeshStandardMaterial): Mesh {
        const first: IVec3 = stringer.points[0];
        const last: IVec3 = stringer.points[stringer.points.length - 1];
        const height: number = Math.abs(last.y - first.y);
        const radius: number = stringer.thickness / 2;

        // 48 facets: a flat lies within 0.3 mm of the round, so the treads touching the pole
        // neither leave a slot nor cut into it.
        const geo: CylinderGeometry = new CylinderGeometry(radius, radius, height, 48);
        // Default CylinderGeometry UV: u wraps circumference [0..1], v along height [0..1].
        // Rescale so grain runs along the pole (v axis) at world scale.

        const uv: BufferAttribute = geo.attributes['uv'] as BufferAttribute;
        const circumference: number = 2 * Math.PI * radius;
        for (let i: number = 0; i < uv.count; i++) {
            uv.setXY(i, uv.getX(i) * (circumference / WOOD_TILE_MM), uv.getY(i) * (height / WOOD_TILE_MM));
        }
        uv.needsUpdate = true;

        const mesh: Mesh = new Mesh(geo, material);
        mesh.position.set(first.x, first.y + height / 2, first.z);
        mesh.castShadow = true;
        mesh.name = 'stringer-center-pole';

        return mesh;
    }

    #buildRailing(railing: IRailingGeometry, balusterMat: MeshStandardMaterial, handrailMat: MeshStandardMaterial): Mesh[] {
        const meshes: Mesh[] = [];
        // Baluster geometry is created per-instance due to varying heights

        // Balusters
        const bw: number = railing.balusterWidth;
        for (let i: number = 0; i < railing.balusters.length; i++) {
            const b: IBalusterGeometry = railing.balusters[i];
            const geo: BoxGeometry = new BoxGeometry(bw, b.height, bw);
            // Grain vertical — balusters are upright cut boards.
            applyBoxWoodUV(geo, [bw, b.height, bw], 'y', i * BOARD_UV_SHIFT);
            const mesh: Mesh = new Mesh(geo, balusterMat);
            mesh.position.set(b.position.x, b.position.y + b.height / 2, b.position.z);
            mesh.castShadow = true;
            mesh.name = `baluster-${railing.side}-${String(i)}`;
            meshes.push(mesh);
        }

        // Posts (thicker) — same depth priority as balusters
        const pw: number = railing.postWidth;
        for (let i: number = 0; i < railing.posts.length; i++) {
            const p: IBalusterGeometry = railing.posts[i];
            const geo: BoxGeometry = new BoxGeometry(pw, p.height, pw);
            applyBoxWoodUV(geo, [pw, p.height, pw], 'y', i * BOARD_UV_SHIFT);
            const mesh: Mesh = new Mesh(geo, balusterMat);
            mesh.position.set(p.position.x, p.position.y + p.height / 2, p.position.z);
            mesh.castShadow = true;
            mesh.name = `post-${railing.side}-${String(i)}`;
            meshes.push(mesh);
        }

        // Handrail — connected segments in 3D, rendered in front of balusters
        // One mitred sweep per chain, so the short pieces over the winders show no corners; a rise straight up a post stays a bar.
        const { width, height } = railing.handrail;
        handrailChains(railing.handrail.points).forEach((chain: IVec3[], i: number) => {
            const name: string = `handrail-${railing.side}-${String(i)}`;
            if (isUprightChain(chain)) {
                meshes.push(...this.#buildRailBar({ from: chain[0], to: chain[1], width, height }, handrailMat, name));
                return;
            }
            const mesh: Mesh = new Mesh(buildHandrailSweepGeometry(chain, width, height), handrailMat);
            mesh.castShadow = true;
            mesh.name = name;
            meshes.push(mesh);
        });

        // Planks (SC-CALC-092): the top one by the handrail species, the bottom one by the balusters'.
        (railing.planks ?? []).forEach((plank: IPlankGeometry, i: number) => {
            const bar: IRailBar = { from: plank.points[0], to: plank.points[1], width: plank.width, height: plank.height };
            const material: MeshStandardMaterial = plank.kind === EPlankKind.Top ? handrailMat : balusterMat;
            meshes.push(...this.#buildRailBar(bar, material, `plank-${plank.kind}-${railing.side}-${String(i)}`));
        });

        return meshes;
    }

    /** A straight bar of a railing between two centre points — a handrail run or a plank; none when shorter than 1 mm. */
    #buildRailBar(bar: IRailBar, material: MeshStandardMaterial, name: string): Mesh[] {
        const segDx: number = bar.to.x - bar.from.x;
        const segDy: number = bar.to.y - bar.from.y;
        const segDz: number = bar.to.z - bar.from.z;
        const len: number = Math.sqrt(segDx * segDx + segDy * segDy + segDz * segDz);
        if (len < 1) {
            return [];
        }

        const geo: BoxGeometry = new BoxGeometry(bar.width, bar.height, len);
        // Grain along the rail length (Z in local box).
        applyBoxWoodUV(geo, [bar.width, bar.height, len], 'z');
        const mesh: Mesh = new Mesh(geo, material);
        mesh.position.set(bar.from.x + segDx / 2, bar.from.y + segDy / 2, bar.from.z + segDz / 2);

        // Rotate to align Z-axis of box with segment direction
        const angleX: number = Math.atan2(segDy, Math.sqrt(segDx * segDx + segDz * segDz));
        const angleY: number = Math.atan2(segDx, segDz);
        mesh.rotation.order = 'YXZ';
        mesh.rotation.y = angleY;
        mesh.rotation.x = -angleX;

        mesh.castShadow = true;
        mesh.name = name;
        return [mesh];
    }

    #getMaterial(matId: string): MeshStandardMaterial {
        const cached: MeshStandardMaterial | undefined = this.#materialCache.get(matId);
        if (cached) {
            return cached;
        }

        const woodDef: IWoodMaterial = WOOD_MATERIALS.find((m: IWoodMaterial) => m.id === matId) ?? WOOD_MATERIALS[0];
        const grain: Texture | null = this.#getGrainTexture(woodDef);

        const mat: MeshStandardMaterial = new MeshStandardMaterial({
            color: new Color(woodDef.color),
            roughness: woodDef.roughness,
            metalness: 0.0,
        });
        if (grain) {
            // The species colour is already in the texels, the material colour stays neutral.
            // No normal map: relief would add highlights, and the grain is meant to stay muted.
            mat.map = grain;
            mat.color = new Color(0xffffff);
        }

        this.#materialCache.set(matId, mat);
        return mat;
    }

    /**
     * Depth-fight priority (coplanar winner):
     *   stair wood (treads / risers / stringers, neutral)
     * > slab                                     (polygonOffset +2 / +4)
     * > handrail                                  (polygonOffset +4 / +8)
     * > balusters / posts                         (polygonOffset +6 / +12)
     * This ordering lets the slab edge hide the handrail and balusters where
     * they pass through it, and keeps the handrail on top of the balusters
     * where a baluster terminates into the handrail.
     */
    #getHandrailMaterial(baseMatId: string): MeshStandardMaterial {
        const key: string = `${baseMatId}__handrail`;
        const cached: MeshStandardMaterial | undefined = this.#materialCache.get(key);
        if (cached) {
            return cached;
        }

        const base: MeshStandardMaterial = this.#getMaterial(baseMatId);
        const mat: MeshStandardMaterial = base.clone();
        mat.polygonOffset = true;
        mat.polygonOffsetFactor = 4;
        mat.polygonOffsetUnits = 8;

        this.#materialCache.set(key, mat);
        return mat;
    }

    #getBalusterMaterial(baseMatId: string): MeshStandardMaterial {
        const key: string = `${baseMatId}__baluster`;
        const cached: MeshStandardMaterial | undefined = this.#materialCache.get(key);
        if (cached) {
            return cached;
        }

        const base: MeshStandardMaterial = this.#getMaterial(baseMatId);
        const mat: MeshStandardMaterial = base.clone();
        mat.polygonOffset = true;
        mat.polygonOffsetFactor = 6;
        mat.polygonOffsetUnits = 12;

        this.#materialCache.set(key, mat);
        return mat;
    }

    #getWarningMaterial(baseMatId: string): MeshStandardMaterial {
        const key: string = `${baseMatId}__warning`;
        const cached: MeshStandardMaterial | undefined = this.#materialCache.get(key);
        if (cached) {
            return cached;
        }

        const baseMat: MeshStandardMaterial = this.#getMaterial(baseMatId);
        const mat: MeshStandardMaterial = baseMat.clone();
        mat.emissive = new Color(0.35, 0.06, 0.06);
        mat.emissiveIntensity = 1.0;

        this.#materialCache.set(key, mat);
        return mat;
    }

    /** One line material for all edges; see `edge-lines.ts` for its depth-fight slot. */
    #getEdgeMaterial(): LineBasicMaterial {
        if (this.#edgeMaterial) {
            return this.#edgeMaterial;
        }
        this.#edgeMaterial = createEdgeMaterial();
        return this.#edgeMaterial;
    }

    /** One grain texture per species, shared by its tread, riser, stringer and railing materials. */
    #getGrainTexture(wood: IWoodMaterial): Texture | null {
        if (this.#textureCache.has(wood.id)) {
            return this.#textureCache.get(wood.id) ?? null;
        }
        const texture: Texture | null = createWoodTexture(globalThis.document, wood.color, wood.id);
        this.#textureCache.set(wood.id, texture);
        return texture;
    }

    public dispose(): void {
        this.#materialCache.forEach((material: MeshStandardMaterial) => {
            material.dispose();
        });
        this.#materialCache.clear();
        this.#edgeMaterial?.dispose();
        this.#edgeMaterial = null;
        this.#textureCache.forEach((texture: Texture | null) => {
            texture?.dispose();
        });
        this.#textureCache.clear();
    }
}
